/**
 * Modal — the one dialog shell. Owns the backdrop (bg-scrim), the layer
 * (--z-modal), Escape/backdrop dismissal, dialog ARIA, a Tab-cycle focus
 * trap, and focus restoration to the trigger on close; content components
 * own only their body. Render it conditionally, matching the codebase idiom:
 * `{open && <Modal onClose={…}>…</Modal>}`.
 *
 * DocumentPreviewModal predates this shell (two-pane custom header) and
 * migrates onto it in a later round.
 *
 * ConfirmDialog is the blocking-question variant — it exists so nothing
 * calls the native confirm(), which ignores theme, tokens and focus order.
 */
import { useEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "./Button.tsx";

const SIZES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-5xl",
} as const;

export type ModalSize = keyof typeof SIZES;

/** Pure so bun:test can cover the size matrix without a DOM. */
export function modalPanelClasses(size: ModalSize): string {
  return `bg-surface rounded-2xl border border-border elevation-3 flex flex-col overflow-hidden w-full max-h-[90vh] outline-none ${SIZES[size]}`;
}

// Escape must close only the topmost dialog when shells ever stack.
const modalStack: symbol[] = [];

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  title: string;
  onClose: () => void;
  size?: ModalSize;
  /** Right-aligned action row; usually Buttons. Omit for informational dialogs. */
  footer?: ReactNode;
  children: ReactNode;
}

export function Modal({ title, onClose, size = "md", footer, children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Latest onClose without re-subscribing the document listener — call sites
  // pass inline arrows, and a re-subscribing effect also re-fired the initial
  // focus, stealing it from inputs inside the dialog on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Backdrop dismissal tracks where the press STARTED: a text-selection drag
  // that ends over the backdrop must not close the dialog.
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    const token = Symbol("modal");
    modalStack.push(token);
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (modalStack[modalStack.length - 1] !== token) return;
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      // Focus trap: aria-modal tells AT the page behind is inert; Tab must agree.
      if (e.key === "Tab" && panelRef.current) {
        const focusable = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (!focusable.length) {
          e.preventDefault();
          panelRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const current = document.activeElement;
        if (e.shiftKey && (current === first || current === panelRef.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && current === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      modalStack.splice(modalStack.indexOf(token), 1);
      opener?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] bg-scrim/60 flex items-center justify-center p-4"
      onMouseDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressStartedOnBackdrop.current) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={modalPanelClasses(size)}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
          <h2 id={titleId} className="text-[15px] font-medium truncate">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-secondary transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Confirm-flow policy, extracted pure so the rejection path is testable
 * without a DOM: close only on success; on failure report and stay open.
 */
export async function runConfirm(
  onConfirm: () => void | Promise<void>,
  onSuccess: () => void,
  onError: (message: string) => void,
): Promise<void> {
  try {
    await onConfirm();
    onSuccess();
  } catch (err) {
    onError(err instanceof Error ? err.message : "The action failed. Try again.");
  }
}

export interface ConfirmDialogProps {
  title: string;
  /** What happens if they proceed — name the object, state the consequence. */
  body: ReactNode;
  confirmLabel: string;
  /** danger = destructive (red confirm); primary = consequential but safe. */
  tone?: "danger" | "primary";
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = "danger",
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setError(null);
    await runConfirm(onConfirm, onClose, setError);
    setBusy(false);
  }

  return (
    <Modal
      title={title}
      // While the action is in flight the dialog must not unmount under it.
      onClose={busy ? () => {} : onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button variant={tone} busy={busy} onClick={confirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[14px] text-text-secondary">{body}</div>
      {error && (
        <div className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
